import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, AlertCircle, Loader2, ShieldCheck } from "lucide-react";
import { submitIntake, trackEvent } from "@/lib/api";
import { TEMAS_CONTACTO, whatsappHref, SITE, CTAS } from "@/content/site";

const TIPOS_URGENCIA = [
  "Notificación o demanda recibida",
  "Fecha próxima o plazo legal",
  "Embargo, retención o aseguramiento",
  "Problema urgente de vivienda",
  "Otro asunto con fecha encima",
];

interface Props {
  variante: "completo" | "prioritario";
}

export function IntakeForm({ variante }: Props) {
  const [params] = useSearchParams();
  const prioritario = variante === "prioritario";

  const [form, setForm] = useState({
    nombre: "",
    medio_contacto: "correo",
    correo: "",
    telefono: "",
    tema_general: prioritario ? "" : params.get("tema") || "",
    ubicacion_asunto: "",
    fecha_proxima: "",
    descripcion: "",
  });
  const [acepta, setAcepta] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [estado, setEstado] = useState<"idle" | "sending" | "ok" | "error">("idle");
  const [serverMsg, setServerMsg] = useState("");
  const [folio, setFolio] = useState("");

  const urgencia = prioritario || params.get("urgencia") === "prioritaria" ? "prioritaria" : "normal";
  const origen = prioritario ? "prioritaria" : params.get("origen") === "orientador" ? "orientador" : "contacto";
  const t = prioritario ? "prioritaria" : "contacto";

  const set = (k: string, v: string) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: "" }));
  };

  function validate(): Record<string, string> {
    const errs: Record<string, string> = {};
    if (form.nombre.trim().length < 2) errs.nombre = "Escribe tu nombre.";
    const correo = form.correo.trim();
    const tel = form.telefono.trim();
    if (correo && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(correo)) errs.correo = "Revisa el formato del correo.";
    if (tel && !/^[0-9+\-\s()]{7,20}$/.test(tel)) errs.telefono = "Revisa el teléfono: solo números, espacios y + - ( ).";
    if (form.medio_contacto === "correo" && !correo) errs.correo = "Necesitamos tu correo para contactarte.";
    if (form.medio_contacto !== "correo" && !tel) errs.telefono = "Necesitamos tu teléfono para contactarte.";
    if (!correo && !tel) errs.correo = "Dinos al menos un correo o un teléfono.";
    if (form.tema_general.trim().length < 3) errs.tema_general = prioritario ? "Elige el tipo de asunto." : "Elige el tema que más se acerque.";
    if (form.ubicacion_asunto.trim().length < 2) errs.ubicacion_asunto = "Indica la ciudad o estado.";
    if (!acepta) errs.acepta_privacidad = "Necesitamos tu consentimiento para procesar tus datos.";
    return errs;
  }

  async function onSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      trackEvent("form_error", { origen, etapa: "cliente" });
      return;
    }
    setEstado("sending");
    setServerMsg("");
    try {
      const res = await submitIntake({ ...form, urgencia, origen, acepta_privacidad: acepta });
      setFolio(res.id);
      setEstado("ok");
      trackEvent("form_success", { origen });
    } catch (e: any) {
      setServerMsg(e?.message || "No pudimos registrar tu solicitud. Inténtalo de nuevo.");
      if (e?.fields) setErrors(e.fields);
      setEstado("error");
      trackEvent("form_error", { origen, etapa: "servidor" });
    }
  }

  if (estado === "ok") {
    return (
      <div className="border border-hairline bg-white p-8 sm:p-10" data-testid={`${t}-form-exito`}>
        <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-salvia-light text-salvia">
          <Check className="h-6 w-6" />
        </span>
        <h3 className="mt-6 font-serif text-3xl text-ink-deep">Recibimos tu solicitud</h3>
        <p className="mt-3 text-ink-muted">
          Tu folio de referencia es <span className="font-mono text-sm text-azul">{folio.slice(0, 8).toUpperCase()}</span>.
          {urgencia === "prioritaria"
            ? " La revisamos con prioridad y te contactamos por el medio que elegiste, en horario hábil."
            : " Te contactamos por el medio que elegiste, en horario hábil, para agendar la llamada de disponibilidad."}
        </p>
        <p className="mt-4 text-sm text-ink-muted">
          Si tu asunto ya no puede esperar, escríbenos directamente por WhatsApp:{" "}
          <a href={whatsappHref()} className="font-medium text-azul underline underline-offset-4" data-testid={`${t}-exito-whatsapp`}>
            {CTAS.secundaria.label}
          </a>
        </p>
        <p className="mt-6 border-t border-hairline pt-4 font-mono text-xs text-ink-muted">
          {SITE.disponibilidad}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate className="border border-hairline bg-white p-6 sm:p-8" data-testid={`${t}-form`}>
      {prioritario && (
        <p className="mb-6 flex items-start gap-2 border-l-2 border-azul bg-azul-light/50 p-4 text-sm text-ink-text">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-azul" />
          Este formulario marca tu solicitud como <strong>prioritaria</strong>. No sustituye una notificación oficial ni inicia una representación automática.
        </p>
      )}

      <div className="space-y-5">
        <div>
          <label htmlFor={`${t}-nombre`} className="field-label">Nombre</label>
          <input
            id={`${t}-nombre`}
            data-testid={`${t}-nombre-input`}
            className={`field ${errors.nombre ? "field-error" : ""}`}
            placeholder="Tu nombre y apellido"
            value={form.nombre}
            onChange={(e) => set("nombre", e.target.value)}
            autoComplete="name"
          />
          {errors.nombre && <p className="field-hint text-destructive" data-testid={`${t}-nombre-error`}>{errors.nombre}</p>}
        </div>

        <div>
          <label htmlFor={`${t}-medio`} className="field-label">¿Cómo prefieres que te contactemos?</label>
          <select
            id={`${t}-medio`}
            data-testid={`${t}-medio-select`}
            className="field"
            value={form.medio_contacto}
            onChange={(e) => set("medio_contacto", e.target.value)}
          >
            <option value="correo">Correo electrónico</option>
            <option value="telefono">Llamada telefónica</option>
            <option value="whatsapp">WhatsApp</option>
          </select>
        </div>

        {form.medio_contacto === "correo" ? (
          <div>
            <label htmlFor={`${t}-correo`} className="field-label">Correo electrónico</label>
            <input
              id={`${t}-correo`}
              type="email"
              inputMode="email"
              data-testid={`${t}-correo-input`}
              className={`field ${errors.correo ? "field-error" : ""}`}
              placeholder="tucorreo@ejemplo.com"
              value={form.correo}
              onChange={(e) => set("correo", e.target.value)}
              autoComplete="email"
            />
            {errors.correo && <p className="field-hint text-destructive" data-testid={`${t}-correo-error`}>{errors.correo}</p>}
          </div>
        ) : (
          <div>
            <label htmlFor={`${t}-telefono`} className="field-label">Teléfono (con lada)</label>
            <input
              id={`${t}-telefono`}
              type="tel"
              inputMode="tel"
              data-testid={`${t}-telefono-input`}
              className={`field ${errors.telefono ? "field-error" : ""}`}
              placeholder="52 1 55 0000 0000"
              value={form.telefono}
              onChange={(e) => set("telefono", e.target.value)}
              autoComplete="tel"
            />
            {errors.telefono && <p className="field-hint text-destructive" data-testid={`${t}-telefono-error`}>{errors.telefono}</p>}
            <p className="field-hint">Si prefieres, agrega también tu correo: {""}
              <button type="button" className="text-azul underline underline-offset-4" data-testid={`${t}-agregar-correo`} onClick={() => set("medio_contacto", "correo")}>
                usar correo
              </button>
            </p>
          </div>
        )}

        <div>
          <label htmlFor={`${t}-tema`} className="field-label">
            {prioritario ? "¿Qué tipo de asunto es?" : "Tema general"}
          </label>
          <select
            id={`${t}-tema`}
            data-testid={`${t}-tema-select`}
            className={`field ${errors.tema_general ? "field-error" : ""}`}
            value={form.tema_general}
            onChange={(e) => set("tema_general", e.target.value)}
          >
            <option value="">Elige una opción</option>
            {(prioritario ? TIPOS_URGENCIA : TEMAS_CONTACTO).map((tema) => (
              <option key={tema} value={tema}>{tema}</option>
            ))}
          </select>
          {errors.tema_general && <p className="field-hint text-destructive" data-testid={`${t}-tema-error`}>{errors.tema_general}</p>}
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <label htmlFor={`${t}-ubicacion`} className="field-label">Ubicación del asunto</label>
            <input
              id={`${t}-ubicacion`}
              data-testid={`${t}-ubicacion-input`}
              className={`field ${errors.ubicacion_asunto ? "field-error" : ""}`}
              placeholder="Ciudad o estado"
              value={form.ubicacion_asunto}
              onChange={(e) => set("ubicacion_asunto", e.target.value)}
              autoComplete="address-level1"
            />
            {errors.ubicacion_asunto && <p className="field-hint text-destructive" data-testid={`${t}-ubicacion-error`}>{errors.ubicacion_asunto}</p>}
          </div>
          <div>
            <label htmlFor={`${t}-fecha`} className="field-label">
              {prioritario ? "Fecha o plazo próximo" : "Fecha próxima (si existe)"}
            </label>
            <input
              id={`${t}-fecha`}
              type="date"
              data-testid={`${t}-fecha-input`}
              className={`field ${errors.fecha_proxima ? "field-error" : ""}`}
              value={form.fecha_proxima}
              onChange={(e) => set("fecha_proxima", e.target.value)}
            />
            {errors.fecha_proxima && <p className="field-hint text-destructive" data-testid={`${t}-fecha-error`}>{errors.fecha_proxima}</p>}
          </div>
        </div>

        <div>
          <label htmlFor={`${t}-descripcion`} className="field-label">
            {prioritario ? "Cuéntanos lo urgente (opcional)" : "Descripción breve (opcional)"}
          </label>
          <textarea
            id={`${t}-descripcion`}
            rows={4}
            data-testid={`${t}-descripcion-textarea`}
            className={`field resize-y ${errors.descripcion ? "field-error" : ""}`}
            placeholder={prioritario ? "Qué recibiste o qué fecha tienes, en una o dos líneas." : "Dos líneas bastan. Los detalles los vemos en la llamada."}
            value={form.descripcion}
            onChange={(e) => set("descripcion", e.target.value)}
            maxLength={2000}
          />
          {errors.descripcion && <p className="field-hint text-destructive">{errors.descripcion}</p>}
        </div>

        <div>
          <label htmlFor={`${t}-privacidad`} className="flex cursor-pointer items-start gap-3 text-sm text-ink-text">
            <input
              id={`${t}-privacidad`}
              type="checkbox"
              data-testid={`${t}-privacidad-checkbox`}
              className="mt-1 h-4 w-4 accent-[#1E355E]"
              checked={acepta}
              onChange={(e) => {
                setAcepta(e.target.checked);
                setErrors((er) => ({ ...er, acepta_privacidad: "" }));
              }}
            />
            <span>
              Leí y acepto el{" "}
              <Link to="/privacidad" className="font-medium text-azul underline underline-offset-4" data-testid={`${t}-privacidad-link`}>
                aviso de privacidad
              </Link>
              . Usaremos tus datos solo para atender tu solicitud.
            </span>
          </label>
          {errors.acepta_privacidad && <p className="field-hint text-destructive" data-testid={`${t}-privacidad-error`}>{errors.acepta_privacidad}</p>}
        </div>

        {estado === "error" && (
          <div className="border border-destructive/30 bg-destructive/5 p-4" data-testid={`${t}-form-error-general`}>
            <p className="flex items-start gap-2 text-sm text-destructive">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              {serverMsg}
            </p>
            <p className="mt-2 text-sm text-ink-muted">
              Tu solicitud no quedó registrada. Puedes intentarlo otra vez o escribirnos por{" "}
              <a href={whatsappHref()} className="font-medium text-azul underline underline-offset-4" data-testid={`${t}-error-whatsapp`}>
                WhatsApp
              </a>
              .
            </p>
          </div>
        )}

        <button
          type="submit"
          data-testid={`${t}-form-submit-button`}
          disabled={estado === "sending"}
          className="btn-primary w-full disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
        >
          {estado === "sending" ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Enviando…
            </>
          ) : (
            <>
              <ShieldCheck className="h-4 w-4" />
              {prioritario ? "Enviar solicitud prioritaria" : "Enviar mi situación"}
            </>
          )}
        </button>
        <p className="font-mono text-xs text-ink-muted">
          {SITE.disponibilidad} · Sin compromiso de contratación.
        </p>
      </div>
    </form>
  );
}
